<template>
  <div class="login-view">
    <!-- Welcome header -->
    <div class="auth-welcome">
      <p class="growth-eyebrow">
        {{ $t('refresh.auth-kicker') }}
      </p>
      <h1 class="welcome-title">
        {{ $t('auth.welcome-back') }}
      </h1>
      <p class="welcome-subtitle">
        {{ $t('refresh.login-description') }}
      </p>
    </div>

    <!-- Magic Link Form -->
    <div
      v-if="showMagicLinkForm"
      class="magic-link-section"
    >
      <div class="form-divider mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.magic-link') }}</span>
        <div class="divider-line" />
      </div>

      <!-- Success state -->
      <div v-if="magicLinkSent">
        <AlertNotification
          type="success"
          :inline="true"
          :title="$t('auth.magic-link-sent')"
          :message="$t('auth.magic-link-sent-message', { email: magicLinkEmail })"
        />
        <div class="has-text-centered mt-4">
          <button
            type="button"
            class="back-link"
            @click="resetMagicLink"
          >
            <i class="fas fa-arrow-left mr-1" />
            {{ $t('auth.use-password-instead') }}
          </button>
        </div>
      </div>

      <!-- Request form -->
      <form
        v-else
        @submit.prevent="submitMagicLink"
      >
        <p class="subtitle has-text-centered is-6 mb-4">
          {{ $t('auth.magic-link-description') }}
        </p>

        <div class="indiefy-field">
          <BaseInput
            v-model="magicLinkEmail"
            :label="$t('auth.email')"
            :placeholder="$t('auth.email')"
            type="email"
            data-test="magic-link-email-input"
            :has-error="!!formErrors.magicLinkEmail"
            :error-message="formErrors.magicLinkEmail"
            required
            autocomplete="email"
            @blur="validateMagicLinkEmail"
          />
        </div>

        <AlertNotification
          v-if="magicLinkError"
          type="warning"
          :inline="true"
          :message="$t('auth.magic-link-error')"
        />

        <div class="field mt-5 has-text-centered">
          <BaseButton
            type="submit"
            variant="primary"
            is-fullwidth
            :is-loading="magicLinkLoading"
            data-test="submit-magic-link-button"
            icon="fas fa-wand-magic-sparkles"
          >
            {{ $t('auth.send-magic-link') }}
          </BaseButton>
        </div>

        <div class="has-text-centered mt-4">
          <button
            type="button"
            class="back-link"
            @click="resetMagicLink"
          >
            <i class="fas fa-arrow-left mr-1" />
            {{ $t('auth.use-password-instead') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Two-Factor Auth Form -->
    <div
      v-else-if="showTwoFactorForm"
      class="two-factor-section"
    >
      <div class="form-divider mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.verification') }}</span>
        <div class="divider-line" />
      </div>

      <p class="subtitle has-text-centered is-6 mb-4">
        {{ $t(twoFactorVerified ? 'auth.two-factor-verified' : 'auth.two-factor-prompt') }}
      </p>

      <form
        :aria-busy="twoFactorLoading"
        @submit.prevent="submitTwoFactorCode"
      >
        <AlertNotification
          v-if="twoFactorError"
          type="error"
          :message="twoFactorError"
          :dismissible="false"
          inline
          class="mb-4"
          data-test="2fa-error"
        />
        <div
          v-if="!twoFactorVerified"
          class="indiefy-field"
        >
          <BaseInput
            v-model="twoFactorCode"
            :label="$t('auth.two-factor-code')"
            :placeholder="$t('auth.enter-two-factor-code')"
            type="text"
            inputmode="numeric"
            data-test="2fa-code-input"
            :disabled="twoFactorLoading"
            :has-error="!!formErrors.twoFactorCode"
            :error-message="formErrors.twoFactorCode"
            required
            autocomplete="one-time-code"
            @blur="validateTwoFactorCode"
          />
          <div class="code-input-hint mt-2">
            <span class="has-text-grey is-size-7">
              <i class="fas fa-info-circle mr-1" />
              {{ $t('auth.check-email-or-phone') }}
            </span>
          </div>
        </div>

        <div class="field mt-5 has-text-centered">
          <BaseButton
            type="submit"
            variant="primary"
            is-fullwidth
            :is-loading="twoFactorLoading"
            data-test="submit-2fa-button"
            icon="fas fa-shield-alt"
          >
            {{ $t(twoFactorVerified ? 'auth.two-factor-continue' : 'auth.verify') }}
          </BaseButton>
          <p
            v-if="twoFactorLoading"
            role="status"
            class="mt-2 text-sm"
            data-test="2fa-progress"
          >
            {{ $t(twoFactorVerified ? 'auth.two-factor-opening' : 'auth.two-factor-verifying') }}
          </p>
        </div>

        <div
          v-if="!twoFactorVerified"
          class="has-text-centered mt-4"
        >
          <button
            type="button"
            class="back-link"
            :disabled="twoFactorLoading"
            @click="resetLogin"
          >
            <i class="fas fa-arrow-left mr-1" />
            {{ $t('general.back') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Main Login Form -->
    <div
      v-else
      class="login-form-section"
    >
      <form @submit.prevent="submitLogin">
        <div class="indiefy-field">
          <BaseInput
            v-model="username"
            :label="$t('auth.email')"
            :placeholder="$t('auth.email')"
            type="email"
            data-test="email-input"
            :has-error="!!formErrors.username"
            :error-message="formErrors.username"
            required
            autocomplete="username"
            @blur="validateUsername"
          />
        </div>

        <div class="indiefy-field">
          <BaseInput
            v-model="password"
            :label="$t('auth.password')"
            :placeholder="$t('auth.password')"
            type="password"
            data-test="password-input"
            :has-error="!!formErrors.password"
            :error-message="formErrors.password"
            required
            autocomplete="current-password"
            @blur="validatePassword"
          />
        </div>

        <!-- Captcha (placed behind/below the password field) -->
        <div
          v-if="captchaImageUrl"
          class="indiefy-field mt-4"
        >
          <div class="captcha-container">
            <div class="captcha-wrapper p-3 mb-3 is-flex is-align-items-center is-justify-content-space-between">
              <div v-html="captchaImageUrl" />
              <button
                type="button"
                class="refresh-captcha-btn"
                :title="$t('refresh.refresh-captcha')"
                @click="refreshCaptcha"
              >
                <i class="fas fa-sync-alt" />
              </button>
            </div>
          </div>
          <BaseInput
            v-model="captchaValue"
            :label="$t('auth.captcha')"
            :placeholder="$t('auth.enter-captcha')"
            type="text"
            data-test="captcha-input"
            :has-error="!!formErrors.captcha"
            :error-message="formErrors.captcha"
            required
            @blur="validateCaptcha"
          />
        </div>

        <div class="field mt-4 options-row">
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <label class="checkbox remember-me is-flex is-align-items-center">
              <span class="custom-checkbox">
                <input
                  v-model="rememberMe"
                  type="checkbox"
                  class="mr-2"
                >
              </span>
              <span>
                {{ $t('auth.remember-me') }}
              </span>
            </label>

            <router-link
              :to="{ name: 'forgot-password' }"
              class="forgot-password-link"
            >
              {{ $t('auth.forgot-password') }}
            </router-link>
          </div>
        </div>

        <AlertNotification
          v-if="loginError"
          type="warning"
          :inline="true"
          :message="$t(`login.${loginError}`)"
        />

        <div class="mt-5 has-text-centered">
          <BaseButton
            type="submit"
            variant="primary"
            is-fullwidth
            :is-loading="isLoading"
            data-test="login-button"
            is-3d
          >
            {{ $t('auth.sign-in') }}
          </BaseButton>
        </div>

        <div class="has-text-centered mt-3 magic-link-toggle">
          <button
            type="button"
            class="magic-link-link"
            data-test="open-magic-link"
            @click="openMagicLink"
          >
            <i class="fas fa-wand-magic-sparkles mr-1" />
            {{ $t('auth.sign-in-with-magic-link') }}
          </button>
        </div>

        <div
          v-if="authError"
          class="error-container mt-4 p-3"
        >
          <i class="fas fa-exclamation-circle mr-2" />
          {{ authError }}
        </div>
      </form>

      <div class="sign-up-prompt mt-5">
        <div class="form-divider mb-4">
          <div class="divider-line" />
          <span class="divider-text">{{ $t('auth.or-continue-with') }}</span>
          <div class="divider-line" />
        </div>

        <div class="social-login-buttons mb-4 is-flex">
          <button
            class="social-button google"
            :disabled="isLoading"
            @click="loginWithGoogle"
          >
            <i
              class="fab fa-google"
              aria-hidden="true"
            /><span>Google</span>
          </button>
          <button
            class="social-button apple"
            :disabled="isLoading"
            @click="loginWithApple"
          >
            <i
              class="fab fa-apple"
              aria-hidden="true"
            /><span>Apple</span>
          </button>
          <!-- <button class="social-button facebook">
            <i class="fab fa-facebook-f" />
          </button> -->
          <!-- <button class="social-button twitter">
            <i class="fab fa-twitter" />
          </button> -->
        </div>

        <div class="has-text-centered">
          <p class="mb-2">
            {{ $t('auth.no-account') }}
          </p>
          <router-link
            :to="{ name: 'signup', query: { redirect: route.query.redirect } }"
            class="sign-up-button"
          >
            {{ $t('auth.sign-up') }} <i class="fas fa-arrow-right ml-1" />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import AlertNotification from '@/components/ui/AlertNotification.vue';
import { useI18n } from 'vue-i18n';
import { usePageSeo } from '@/composables/usePageSeo';
import authService from '@/services/authService';

usePageSeo({
  titleKey: 'auth.login-title',
  descriptionKey: 'auth.login-description',
});

// Router and route
const router = useRouter();
const route = useRoute();

// Auth store
const authStore = useAuthStore();
const isLoading = computed(() => authStore.isLoading);
const authError = computed(() => authStore.authError);

// i18n
const { t } = useI18n();

// Form state
const loginError = ref('');
const username = ref('');
const password = ref('');
const captchaValue = ref('');
const rememberMe = ref(false);
const twoFactorCode = ref('');
const showTwoFactorForm = ref(false);
const twoFactorLoading = ref(false);
const twoFactorError = ref('');
const twoFactorVerified = ref(false);
const twoFactorBlocked = ref(false);
const formErrors = ref({
  username: '',
  password: '',
  captcha: '',
  twoFactorCode: '',
  magicLinkEmail: '',
});

// Magic link state — passwordless sign-in via emailed link.
// Intentionally tracked separately from the password flow so that captcha,
// 2FA, and login errors don't bleed into the magic-link UI (the endpoint
// doesn't require any of them).
const showMagicLinkForm = ref(false);
const magicLinkEmail = ref('');
const magicLinkLoading = ref(false);
const magicLinkSent = ref(false);
const magicLinkError = ref(false);

// Captcha
const captchaImageUrl = ref('');

// Form validation
const validateUsername = () => {
  if (!username.value) {
    formErrors.value.username = t('errors.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username.value)) {
    formErrors.value.username = t('errors.email-invalid');
  } else {
    formErrors.value.username = '';
  }
};

const validatePassword = () => {
  if (!password.value) {
    formErrors.value.password = t('errors.required');
  } else {
    formErrors.value.password = '';
  }
};

const validateCaptcha = () => {
  if (!captchaValue.value) {
    formErrors.value.captcha = t('errors.required');
  } else {
    formErrors.value.captcha = '';
  }
};

const validateTwoFactorCode = () => {
  twoFactorCode.value = twoFactorCode.value.replace(/\s/g, '');
  if (!twoFactorCode.value) {
    formErrors.value.twoFactorCode = t('errors.required');
  } else {
    formErrors.value.twoFactorCode = '';
  }
};

const validateMagicLinkEmail = () => {
  if (!magicLinkEmail.value) {
    formErrors.value.magicLinkEmail = t('errors.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(magicLinkEmail.value)) {
    formErrors.value.magicLinkEmail = t('errors.email-invalid');
  } else {
    formErrors.value.magicLinkEmail = '';
  }
};

const validateForm = () => {
  validateUsername();
  validatePassword();
  if (captchaImageUrl.value) validateCaptcha();

  return !formErrors.value.username &&
         !formErrors.value.password &&
         (!captchaImageUrl.value || !formErrors.value.captcha);
};

const validateTwoFactorForm = () => {
  validateTwoFactorCode();
  return !formErrors.value.twoFactorCode;
};

// Load captcha from server
const loadCaptcha = async () => {
  try {
    const captchaData = await authStore.getCaptcha();
    if (captchaData) {
      captchaImageUrl.value = captchaData;
    }
  } catch (error) {
    console.error('Failed to load captcha:', error);
    // Fallback to placeholder in case of error
    captchaImageUrl.value = 'https://via.placeholder.com/200x60/f4f4f4/333333?text=CAPTCHA';
  }
};

const refreshCaptcha = () => {
  captchaValue.value = '';
  loadCaptcha();
};

// Form submission
const submitLogin = async () => {
  if (!validateForm()) return;

  loginError.value = '';
  authStore.clearError();

  // Include captcha value from the form with default locale
  const result = await authStore.login(
    username.value,
    password.value,
    captchaValue.value,
  );

  if (result.requiresTwoFactor) {
    showTwoFactorForm.value = true;
  } else if (result.blocked) {
    // User is blocked, redirect to blocked page
    router.push('/account/blocked');
  } else if (result.success) {
    handleRedirect();
  } else {
    loginError.value = result.error;

    // Refresh captcha on failure
    refreshCaptcha();
  }
};

const submitTwoFactorCode = async () => {
  if (twoFactorLoading.value) return;
  if (!twoFactorVerified.value && !validateTwoFactorForm()) return;

  twoFactorError.value = '';
  authStore.clearError();
  twoFactorLoading.value = true;

  try {
    // Once accepted, a one-time code must not be submitted again just because
    // the destination page failed to load. Retry only the navigation.
    if (!twoFactorVerified.value) {
      const result = await authStore.confirmTwoFactor(twoFactorCode.value);
      if (!result?.success && !result?.blocked) {
        twoFactorError.value = result?.errorKey
          ? t(result.errorKey)
          : result?.error || t('auth.two-factor-failed');
        return;
      }
      twoFactorVerified.value = true;
      twoFactorBlocked.value = !!result.blocked;
      twoFactorCode.value = '';
    }

    const failure = await (twoFactorBlocked.value
      ? router.push('/account/blocked')
      : handleRedirect());
    if (failure) twoFactorError.value = t('auth.two-factor-navigation-failed');
  } catch {
    twoFactorError.value = t(twoFactorVerified.value
      ? 'auth.two-factor-navigation-failed'
      : 'auth.two-factor-failed');
  } finally {
    twoFactorLoading.value = false;
  }
};

const resetLogin = () => {
  if (twoFactorLoading.value) return;
  showTwoFactorForm.value = false;
  twoFactorCode.value = '';
  twoFactorError.value = '';
  twoFactorVerified.value = false;
  twoFactorBlocked.value = false;
  formErrors.value.twoFactorCode = '';
  authStore.clearError();
};

const openMagicLink = () => {
  // Prefill with whatever the user already typed in the password form, but
  // never overwrite a value the user already typed into the magic-link field.
  if (!magicLinkEmail.value && username.value) {
    magicLinkEmail.value = username.value;
  }
  magicLinkSent.value = false;
  magicLinkError.value = false;
  formErrors.value.magicLinkEmail = '';
  showMagicLinkForm.value = true;
  authStore.clearError();
};

const resetMagicLink = () => {
  showMagicLinkForm.value = false;
  magicLinkSent.value = false;
  magicLinkError.value = false;
  formErrors.value.magicLinkEmail = '';
};

const submitMagicLink = async () => {
  validateMagicLinkEmail();
  if (formErrors.value.magicLinkEmail) return;

  magicLinkError.value = false;
  magicLinkLoading.value = true;
  try {
    await authService.requestMagicLink({ email: magicLinkEmail.value });
    magicLinkSent.value = true;
  } catch (error) {
    console.error('Magic link request failed:', error);
    magicLinkError.value = true;
  } finally {
    magicLinkLoading.value = false;
  }
};

// Handle redirect after successful login
const handleRedirect = () => {
  const redirectPath = route.query.redirect || '/dashboard';

  // If redirect starts with https://, use location.href for external redirect
  if (typeof redirectPath === 'string' && redirectPath.startsWith('https://')) {
    window.location.href = redirectPath;
  } else {
    return router.push(redirectPath);
  }
};

// Google login
const loginWithGoogle = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithGoogle();

    if (result.requiresTwoFactor) {
      showTwoFactorForm.value = true;
    } else if (result.blocked) {
      // User is blocked, redirect to blocked page
      router.push('/account/blocked');
    } else if (result.success) {
      handleRedirect();
    }
  } catch (error) {
    console.error('Google login error:', error);
    // Error is already set in the store
  }
};

// Apple login
const loginWithApple = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithApple();

    if (result.requiresTwoFactor) {
      showTwoFactorForm.value = true;
    } else if (result.blocked) {
      // User is blocked, redirect to blocked page
      router.push('/account/blocked');
    } else if (result.success) {
      handleRedirect();
    }
  } catch (error) {
    console.error('Apple login error:', error);
    // Error is already set in the store
  }
};

// Lifecycle hooks
onMounted(() => {
  loadCaptcha();
});
</script>
